import { createApi, BaseQueryFn } from '@reduxjs/toolkit/query/react';
import { createEntityAdapter } from '@reduxjs/toolkit';
import {
  INotification,
  INotificationNormalizedResponse,
  INotificationQueryParam,
  TNotificationRawResponse,
  ICreateNotification,
  IGetNotificationPreferenceResponse,
  IGetNotificationPreferenceRequest,
  IUpdateNotificationPreferenceRequest,
  IUpdateNotificationPreferenceResponse,
  IMentionPushNotificationRequest,
  IMentionPushNotificationResponse,
  IGroupedConversationNotificationsResponse,
  IGroupedConversationNotificationsRequest,
  IMarkConversationNotificationsAsReadRequest,
  IMarkConversationNotificationsAsReadResponse,
  INotificationResponse,
} from './typings';
import { QUERIES } from './constants';

export const notificationAdapter = createEntityAdapter({
  selectId: (item: INotification) => item.id,
  sortComparer: (a, b) => b.created_at.localeCompare(a.created_at),
});

export const notificationSelector = notificationAdapter.getSelectors();

const getConversationIdFromNotification = (notification: Partial<INotification>) => {
  const miscellaneous = notification.miscellaneous;
  const directConversationId =
    miscellaneous?.conversationId || miscellaneous?.routeData?.conversationId;

  if (directConversationId) {
    return directConversationId;
  }

  const route = miscellaneous?.route;
  if (!route) {
    return undefined;
  }

  try {
    const url = new URL(route, 'https://kor.local');
    return url.searchParams.get('conversation_id') || undefined;
  } catch {
    return route.match(/[?&]conversation_id=([^&]+)/)?.[1];
  }
};

export const notificationApiService = (baseQuery: BaseQueryFn) => {
  const api = createApi({
    reducerPath: 'notification-api',
    baseQuery,
    tagTypes: [QUERIES.notifications, QUERIES.preferences],
    endpoints: (builder) => ({
      getNotifications: builder.query<INotificationNormalizedResponse, INotificationQueryParam>({
        query: ({ page, pageSize, user_id, single_message }) => ({
          url: 'public/v1/notification/notification',
          params: { page, pageSize, user_id, single_message },
        }),
        providesTags: [QUERIES.notifications],
        transformResponse: (response: TNotificationRawResponse) => ({
          notifications: notificationAdapter.setAll(
            notificationAdapter.getInitialState(),
            response.notifications,
          ),
          total_pages: response.total_pages,
          total_records: response.total_records,
          unread_notifications: response.unread_notifications,
        }),
        forceRefetch: ({ currentArg, previousArg }) => {
          const isSamePage = currentArg?.page === previousArg?.page;
          return !isSamePage;
        },
        serializeQueryArgs: ({ endpointName, queryArgs }) =>
          `${endpointName}-${queryArgs?.user_id}-${queryArgs?.single_message}`,
        merge: (currentState, incomingState) => {
          notificationAdapter.addMany(
            currentState.notifications,
            notificationSelector.selectAll(incomingState.notifications),
          );
          currentState.total_pages = incomingState.total_pages;
          currentState.total_records = incomingState.total_records;
          currentState.unread_notifications = incomingState.unread_notifications;
        },
      }),
      getNotificationPreference: builder.query<
        IGetNotificationPreferenceResponse,
        IGetNotificationPreferenceRequest
      >({
        query: ({ fan_id, creator_id }) => ({
          url: `public/v1/notification/notification_preferences/${fan_id}/${creator_id}`,
        }),
        providesTags: [QUERIES.preferences],
      }),
      getNotificationByType: builder.query<
        INotificationResponse,
        { user_id: string; type: string; creator_id: string; conversation_id?: string }
      >({
        query: ({ user_id, type, creator_id, conversation_id }) => ({
          url: `public/v1/notification/get_notifications_by_type/${user_id}`,
          params: { type, creator_id, ...(conversation_id ? { conversation_id } : {}) },
        }),
        providesTags: (result, error, arg) => [
          { type: QUERIES.notifications, id: `${arg.type}_${arg.user_id}_${arg.creator_id}` },
        ],
      }),
      getNotificationById: builder.query<INotification, string>({
        query: (id) => ({
          url: `public/v1/notification/notification/${id}`,
        }),
        providesTags: (result, error, id) => [{ type: QUERIES.notifications, id }],
      }),
      createNotification: builder.mutation<INotification, ICreateNotification>({
        query: (notificationData) => ({
          url: 'public/v1/notification/notification',
          method: 'POST',
          body: notificationData,
        }),
        invalidatesTags: [QUERIES.notifications],
      }),
      updateNotification: builder.mutation<
        INotification,
        INotification & {
          queryParams: INotificationQueryParam;
          notificationType?: 'post' | 'challenge';
          creator_id?: string;
        }
      >({
        query: ({ id, notificationType, creator_id, ...rest }) => ({
          url: `public/v1/forum/message/${id}`,
          method: 'PATCH',
          body: rest,
        }),
        invalidatesTags: (result, error, args) => [
          QUERIES.notifications,
          {
            type: QUERIES.notifications,
            id: `${args.notificationType}_${args.user_id}_${args.creator_id}`,
          },
        ],
        async onQueryStarted({ id, ...rest }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            api.util.updateQueryData('getNotifications', rest.queryParams, (draft) => {
              const notification = notificationSelector.selectById(draft.notifications, id);
              if (!notification) return;

              notificationAdapter.updateOne(draft.notifications, {
                id,
                changes: {
                  ...notification,
                  ...rest,
                },
              });

              // Optimistically update unread count when marking a notification as read
              const isMarkingAsRead = rest.read === true && notification.read !== true;
              if (isMarkingAsRead && typeof draft.unread_notifications === 'number') {
                draft.unread_notifications = Math.max(0, draft.unread_notifications - 1);
              }
            }),
          );

          try {
            await queryFulfilled;
          } catch (error) {
            patchResult.undo();
            // The row may still be read on the server: PATCH /forum/message/:id persists
            // `read` before it can fail on unrelated work, so undoing alone can put the
            // count back up on a notification the user has already opened. RTKQ skips
            // `invalidatesTags` for a rejected mutation, so refetch explicitly.
            dispatch(api.util.invalidateTags([QUERIES.notifications]));
          }
        },
      }),
      markAllAsRead: builder.mutation<
        { success: boolean },
        string | { user_id: string; single_message?: boolean }
      >({
        query: (arg) => {
          const user_id = typeof arg === 'string' ? arg : arg.user_id;
          const single_message = typeof arg === 'string' ? undefined : arg.single_message;
          return {
            url: `public/v1/notification/mark_all_as_read/${user_id}`,
            method: 'PUT',
            body: single_message !== undefined ? { single_message } : undefined,
          };
        },
        invalidatesTags: (result, error, arg) => {
          const user_id = typeof arg === 'string' ? arg : arg.user_id;
          const single_message = typeof arg === 'string' ? undefined : arg.single_message;
          if (single_message === true) {
            // Only message notifications cache
            return [
              QUERIES.notifications,
              { type: QUERIES.notifications, id: `getNotifications-${user_id}-true` },
            ];
          }
          if (single_message === false || single_message === undefined) {
            // Non-message buckets: invalidate both explicit false and default undefined caches
            return [
              QUERIES.notifications,
              { type: QUERIES.notifications, id: `getNotifications-${user_id}-false` },
              { type: QUERIES.notifications, id: `getNotifications-${user_id}-undefined` },
            ];
          }
          // Fallback
          return [QUERIES.notifications];
        },
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          const user_id = typeof arg === 'string' ? arg : arg.user_id;
          const single_message = typeof arg === 'string' ? undefined : arg.single_message;

          const targets: Array<boolean | undefined> =
            single_message === true ? [true] : [false, undefined];

          const appliedPatches = targets.map((sm) =>
            dispatch(
              api.util.updateQueryData(
                'getNotifications',
                { user_id, single_message: sm, page: 1, pageSize: 20 } as any,
                (draft) => {
                  // Mark all currently cached items as read
                  const all = notificationSelector.selectAll(draft.notifications);
                  for (const n of all) {
                    if (!n.read) {
                      notificationAdapter.updateOne(draft.notifications, {
                        id: n.id,
                        changes: { ...n, read: true },
                      });
                    }
                  }
                  // Set unread count to 0 for this bucket
                  if (typeof draft.unread_notifications === 'number') {
                    draft.unread_notifications = 0;
                  }
                },
              ),
            ),
          );

          try {
            await queryFulfilled;
          } catch (e) {
            // Revert optimistic updates on failure
            appliedPatches.forEach((p) => p.undo && p.undo());
          }
        },
      }),
      // Update / create preference
      updateNotificationPreference: builder.mutation<
        IUpdateNotificationPreferenceResponse,
        IUpdateNotificationPreferenceRequest
      >({
        query: ({ fan_id, creator_id, notification_type, enabled }) => ({
          url: `public/v1/notification/notification_preferences`,
          method: 'PATCH',
          body: { fan_id, creator_id, notification_type, enabled },
        }),
        invalidatesTags: (result, error, args) => [
          QUERIES.preferences,
          {
            type: QUERIES.preferences,
            id: `${args.fan_id}_${args.notification_type}_${args.creator_id}`,
          },
        ],
      }),
      markNotificationsByType: builder.mutation<
        { success: true },
        {
          user_id: string;
          type: string;
          creator_id?: string;
          global_mark?: boolean;
          conversation_id?: string;
        }
      >({
        query: ({ user_id, type, creator_id, global_mark, conversation_id }) => {
          const params = new URLSearchParams();
          if (global_mark !== undefined) params.append('global_mark', String(global_mark));
          if (conversation_id) params.append('conversation_id', conversation_id);
          const queryString = params.toString();
          return {
            url: `public/v1/notification/mark_notifications_by_type/${user_id}${
              queryString ? `?${queryString}` : ''
            }`,
            method: 'PUT',
            body: { type, creator_id, ...(conversation_id ? { conversation_id } : {}) },
          };
        },
        invalidatesTags: (result, error, args) => [
          // When global_mark is true, invalidate ALL notification caches
          ...(args.global_mark ? [QUERIES.notifications] : []),
          // Only invalidate the specific notification type cache
          ...(args.type === 'message'
            ? [{ type: QUERIES.notifications, id: `getNotifications-${args.user_id}-true` }]
            : [{ type: QUERIES.notifications, id: `getNotifications-${args.user_id}-false` }]),
          // Also invalidate the type-specific cache (with conversation_id if provided)
          {
            type: QUERIES.notifications,
            id: `${args.type}_${args.user_id}_${args.creator_id}${
              args.conversation_id ? `_${args.conversation_id}` : ''
            }`,
          },
          // Crew and topic counts are returned from the 'post' type query, so invalidate it too
          ...(args.type === 'crew' || args.type === 'topic'
            ? [{ type: QUERIES.notifications, id: `post_${args.user_id}_${args.creator_id}` }]
            : []),
        ],
      }),

      deleteNotification: builder.mutation<{ success: boolean }, string>({
        query: (id) => ({
          url: `public/v1/notification/notification/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, id) => [{ type: QUERIES.notifications, id }],
      }),
      mentionPushNotification: builder.mutation<
        IMentionPushNotificationResponse,
        IMentionPushNotificationRequest
      >({
        query: (mentionData) => ({
          url: 'public/v1/notification/mention_push_notification',
          method: 'POST',
          body: mentionData,
        }),
        invalidatesTags: [QUERIES.notifications],
      }),

      // Grouped conversation notifications
      getGroupedConversationNotifications: builder.query<
        IGroupedConversationNotificationsResponse,
        IGroupedConversationNotificationsRequest
      >({
        query: ({ user_id }) => ({
          url: 'public/v1/notification/grouped_by_conversation',
          params: { user_id },
        }),
        providesTags: (result, error, args) => [
          { type: QUERIES.notifications, id: `grouped_conversations_${args.user_id}` },
        ],
      }),

      // Mark conversation notifications as read
      markConversationNotificationsAsRead: builder.mutation<
        IMarkConversationNotificationsAsReadResponse,
        IMarkConversationNotificationsAsReadRequest
      >({
        query: ({ user_id, creator_id, conversation_id, bulk_update }) => ({
          url: 'public/v1/notification/update_bulk_read_messages',
          method: 'PUT',
          body: { user_id, creator_id, conversation_id, ...(bulk_update ? { bulk_update } : {}) },
        }),
        invalidatesTags: (result, error, args) => [
          QUERIES.notifications,
          { type: QUERIES.notifications, id: `grouped_conversations_${args.user_id}` },
          { type: QUERIES.notifications, id: `getNotifications-${args.user_id}-true` },
        ],
        async onQueryStarted(
          { user_id, conversation_id, bulk_update },
          { dispatch, queryFulfilled, getState },
        ) {
          const matchingQueries = api.util.selectInvalidatedBy(getState() as any, [
            QUERIES.notifications,
          ]);
          const isBulkUpdate = bulk_update === 'true';

          const patches = matchingQueries
            .filter((query) => query.endpointName === 'getNotifications')
            .map((query) =>
              dispatch(
                api.util.updateQueryData(
                  'getNotifications',
                  query.originalArgs as INotificationQueryParam,
                  (draft) => {
                    const args = query.originalArgs as INotificationQueryParam;
                    if (args?.user_id !== user_id) return;

                    let markedReadCount = 0;
                    const notifications = notificationSelector.selectAll(draft.notifications);

                    for (const notification of notifications) {
                      const shouldMark =
                        notification.miscellaneous?.createdFrom === 'message' &&
                        (isBulkUpdate ||
                          (!!conversation_id &&
                            getConversationIdFromNotification(notification) === conversation_id));

                      if (shouldMark && !notification.read) {
                        notificationAdapter.updateOne(draft.notifications, {
                          id: notification.id,
                          changes: { ...notification, read: true },
                        });
                        markedReadCount += 1;
                      }
                    }

                    if (markedReadCount && typeof draft.unread_notifications === 'number') {
                      draft.unread_notifications = Math.max(
                        0,
                        draft.unread_notifications - markedReadCount,
                      );
                    }
                  },
                ),
              ),
            );

          try {
            await queryFulfilled;
          } catch {
            patches.forEach((patch) => patch.undo());
          }
        },
      }),
    }),
  });

  return api;
};
